import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { serviceSentence, requestConversation } from "@/lib/conversation";

const SERVICES = [
  { name: "Brand Campaigns", body: "Campaigns that people remember long after they\u2019re over." },
  { name: "Advertising", body: "Thirty seconds that earn a place in someone\u2019s day." },
  { name: "Brand Films", body: "The story of who you are, told the way it deserves." },
  { name: "Product Films", body: "Films that turn a product into something people want." },
  { name: "Fashion", body: "A point of view, not just a look." },
  { name: "Music Videos", body: "One song. One idea. Something worth replaying." },
  { name: "Influencer Campaigns", body: "Stories that feel like recommendations, not ads." },
  { name: "Offline Campaigns", body: "Streets, walls and moments people stop for." },
  { name: "Corporate Films", body: "Stories that make people believe in the people behind the brand." },
  { name: "Testimonials", body: "Real people, real words, filmed with real care." },
  { name: "Events", body: "The energy of the room, kept alive for everyone who missed it." },
  { name: "Product Shoots", body: "Every angle considered. Every frame deliberate." },
  { name: "Social Media Content", body: "Made for the scroll. Built to stop it." },
  { name: "Documentary", body: "True stories, told with patience and craft." },
  { name: "Real Estate", body: "Spaces filmed the way they feel to walk through." },
  { name: "AI Films", body: "New tools. The same obsession with craft." },
];

export default function Services() {
  const navigate = useNavigate();

  const startWith = (name) => {
    requestConversation(serviceSentence(name));
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-white text-ink flex flex-col">
      <Nav />

      <main className="flex-1">
        <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-24 md:pt-40 pb-12 md:pb-20">
          <Reveal>
            <div className="eyebrow eyebrow-denim mb-8">Services</div>
          </Reveal>
          <Reveal as="h1" delay={80} className="font-display font-extrabold text-[44px] sm:text-[64px] md:text-[88px] tracking-[-0.045em] leading-[0.96] text-ink max-w-[1100px]">
            Every <span className="font-serif-italic font-light">story</span>.
            <br />
            Every format.
          </Reveal>
          <Reveal delay={180}>
            <p className="text-muted-ink text-[16px] md:text-[18px] mt-8 max-w-[640px] leading-[1.7]">
              If it helps build your brand, we can produce it.
            </p>
          </Reveal>
        </section>

        <section className="max-w-[1280px] mx-auto px-6 md:px-10 pb-32 md:pb-48">
          <Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {SERVICES.map((s, i) => (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => startWith(s.name)}
                  data-testid={`service-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                  style={{ transitionDelay: `${Math.min(i * 40, 320)}ms` }}
                  className="group bg-white border border-bnm rounded-xl p-6 md:p-7 flex flex-col items-start gap-3 text-left min-h-[150px] cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:border-[#d6d6d6] hover:shadow-[0_10px_30px_rgba(10,10,10,0.06)]"
                  aria-label={`Start a conversation about ${s.name}`}
                >
                  <h3 className="font-display font-semibold text-[17px] md:text-[18px] tracking-[-0.01em] text-ink flex items-center gap-2">
                    {s.name}
                    <ArrowRight className="w-3.5 h-3.5 text-muted-ink opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </h3>
                  <p className="text-muted-ink text-[13.5px] leading-[1.6]">{s.body}</p>
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-20 md:mt-28 flex flex-col items-center text-center gap-6">
              <h3 className="font-display font-extrabold text-[34px] sm:text-[48px] md:text-[64px] tracking-[-0.04em] leading-[1.0]">
                Whatever the format,
                <br />
                <span className="font-serif-italic font-light">one Producer</span>.
              </h3>
              <Link
                to="/"
                onClick={() => requestConversation()}
                className="btn-denim"
                data-testid="services-cta-start"
              >
                <span>Start your project</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </section>
      </main>

      <Footer />
    </div>
  );
}
